import { useApolloClient, useMutation } from '@apollo/client';
import { ResolveBlock } from '@bechtel/client-blocks';
import {
  CacheKeys,
  ErrorBoundary,
  SuspenseLoader,
  useAppUser,
  useBlockPermission,
  useWeldAssignees,
  useWeldOwner,
  useWorkPackageOwner,
  WeldMilestoneType,
} from '@bechtel/client-common';
import { UNCLAIM_WELD } from '@bechtel/client-gql';
import {
  Box,
  Icon,
  IconSize,
  Spinner,
  SpinnerSize,
  Text,
  theme,
  ToastAction,
  ToastType,
  useSideSheetState,
  useToastsDispatch,
} from '@bechtel/gds';
import {
  CommodityAssignee,
  CommodityMilestoneActivity,
  CommodityStatusCode,
  CrewMember,
  MilestoneActivity,
  MilestoneStatusCode,
  MilestoneStatusInput,
  PersonaType,
  UnclaimWeldPayload,
} from '@bechtel/types';
import { t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { addDays, isBefore, parseISO } from 'date-fns';
import { filter, find, get } from 'lodash';
import { FC, Fragment, Suspense, useState } from 'react';
import { WeldStatusMessages } from '~/components';
import { WeldClaim } from '~/components/organisms/claim-weld';
import { WeldSendBackSideSheet } from '~/components/organisms/weld-send-back-sidesheet';
import { useUnapproveFitup, useWeldPermissions } from '~/hooks';
import { useApproveFitup } from '~/hooks/use-approve-fitup';
import { useApproveWeld } from '~/hooks/use-approve-weld';
import { useUnApproveWeld } from '~/hooks/use-unapprove-weld';
import { WeldMilestoneAssign } from './assign/internals';
import { WeldActionError } from './error';
import { WeldActionErrorExt } from './error/types';
import { SecondaryActionButtonStyled, WeldActionButtonStyled, WeldActionUnclamiedButtonStyled } from './styled';
import { WeldActionContentProps, WeldActionProps, WeldNonActionProps, WeldSendBackProps } from './types';
const commonRefetchQueries = ['commodities', 'commodityWeeklyStats', 'commodityDashboardTabStats', 'weldDetails'];
export const refetchQueries = [
  'commodities',
  'commodityStats',
  'commodityStatsPriority',
  'commodityWeeklyStats',
  'commodityStatsClaimedAndUnassignedFWE',
  'commodityStatsRejectedFWE',
  'commodityDashboardTabStats',
  'commodityBlocksStatsFWE',
  'assignToMeStatsFWE',
  'commodityFitupStats',
  'commodityStatsAllPriorityStats',
];
const approveUnapproveFitupCrewQueries = ['commodities', 'commodityStats', 'commodityDashboardTabStats', 'weldDetails'];
const approveUnapproveFitupFWEQueries = [
  'commodities',
  'assignToMeStatsFWE',
  'commodityFitupStats',
  'commodityDashboardTabStats',
  'weldDetails',
];
const reclaimRefetchQueries = ['commodityStats', ...commonRefetchQueries];
const claimRefetchQueries = ['commodityStats', ...commonRefetchQueries];
const claimAndApproveRefetchQueries = ['assignToMeStatsFWE', 'commodityStatsAllPriorityStats', ...commonRefetchQueries];
const unclaimRefetchQueries = ['commodityStats', ...commonRefetchQueries];
const approveWeldRefetchQueries = ['commodityStatsAllPriorityStats', ...commonRefetchQueries];
const unapproveWeldRefetchQueries = [...commonRefetchQueries];
const fitupQueries = () => {
  const dashboardSelectedUser: CrewMember = JSON.parse(sessionStorage.getItem(CacheKeys.DashboardSelectedUser));
  const { user } = useAppUser();
  const currentFitupQueries =
    PersonaType.FIELD_WELD_ENGINEER == dashboardSelectedUser?.persona ||
    PersonaType.FIELD_WELD_ENGINEER == user?.crewMember?.persona
      ? approveUnapproveFitupFWEQueries
      : approveUnapproveFitupCrewQueries;
  return currentFitupQueries;
};

export const ActionContent: FC<WeldActionContentProps> = ({ icon, label }) => {
  return (
    <Fragment>
      <Box mr={{ sm: '400' }} style={{ marginRight: `${theme.spacings[100]}` }}>
        <Icon icon={icon} size={IconSize.MEDIUM} />
      </Box>
      <Text color="grey.1000" size="300" style={{ whiteSpace: 'nowrap' }}>
        {label}
      </Text>
    </Fragment>
  );
};

export const UnclaimAction: FC<WeldActionProps> = ({ disabled, status, customStyle, milestone, weldCommodity }) => {
  const [unclaimWeldError, SetUnlaimWeldError] = useState<WeldActionErrorExt | null>();
  const client = useApolloClient();
  const dispatchToast = useToastsDispatch();
  const [unclaimWeld, { loading: mutationLoading }] = useMutation<
    { unclaimWeldPayload: UnclaimWeldPayload },
    { input: MilestoneStatusInput }
  >(UNCLAIM_WELD, {
    onCompleted: async (data) => {
      const error = get(data, 'unclaimWeld.error');
      if (error) {
        SetUnlaimWeldError(error);
      } else {
        SetUnlaimWeldError(null);
        const toastAction: ToastAction = {
          type: 'ADD_TOAST',
          payload: {
            toastType: ToastType.INFO,
            content: t`Unclaimed Weld successfully`,
            autoDismissTimeout: 5000,
          },
        };
        dispatchToast(toastAction);
        close();
        await client.refetchQueries({
          include: unclaimRefetchQueries,
        });
      }
    },
    onError: (error) => {
      SetUnlaimWeldError(error);
    },
  });
  const closeModal = () => {
    SetUnlaimWeldError(null);
  };
  return (
    <>
      <WeldActionUnclamiedButtonStyled
        status={status}
        data-testid="weld-unclaim-button"
        disabled={mutationLoading || disabled}
        style={customStyle}
        onClick={async () => {
          await unclaimWeld({
            variables: { input: { commodityMilestoneId: milestone.id } },
          });
        }}
      >
        {mutationLoading && <Spinner size={SpinnerSize.SMALL} />}
        {!mutationLoading && <ActionContent label={t`Unclaim`} icon="minus" />}
      </WeldActionUnclamiedButtonStyled>
      {unclaimWeldError && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <WeldActionError error={unclaimWeldError} close={closeModal} commodityId={weldCommodity?.id} />
          </Suspense>
        </ErrorBoundary>
      )}
    </>
  );
};

export const ClaimAction: FC<WeldActionProps> = ({ status, weldCommodity }) => {
  const { user } = useAppUser();

  const { visible: visibleClaimWeld, open: openClaimWeld, close: closeClaimWeld } = useSideSheetState(false);
  return (
    <>
      <WeldActionButtonStyled status={status} data-testid="weld-claim-action" onClick={openClaimWeld}>
        <ActionContent
          label={`${user?.crewMember?.persona === PersonaType.FIELD_WELD_ENGINEER ? t`Claim & Approve` : t`Claim`}`}
          icon="plus"
        />
      </WeldActionButtonStyled>
      {visibleClaimWeld && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <WeldClaim
              weld={weldCommodity}
              isClaimAndApprove={user?.crewMember?.persona === PersonaType.FIELD_WELD_ENGINEER}
              close={closeClaimWeld}
              refetchQueries={
                user?.crewMember?.persona === PersonaType.FIELD_WELD_ENGINEER
                  ? claimAndApproveRefetchQueries
                  : claimRefetchQueries
              }
            />
          </Suspense>
        </ErrorBoundary>
      )}
    </>
  );
};

export const ClaimAndApproveAction: FC<WeldActionProps> = ({ status, weldCommodity }) => {
  const {
    visible: visibleClaimAndApprove,
    open: openClaimAndApprove,
    close: closeClaimAndApprove,
  } = useSideSheetState(false);
  return (
    <>
      <WeldActionButtonStyled status={status} data-testid="weld-claim-action" onClick={openClaimAndApprove}>
        <ActionContent label={t`Claim & Approve`} icon="plus" />
      </WeldActionButtonStyled>
      {visibleClaimAndApprove && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <WeldClaim
              isClaimAndApprove={true}
              close={closeClaimAndApprove}
              weld={weldCommodity}
              refetchQueries={claimAndApproveRefetchQueries}
            />
          </Suspense>
        </ErrorBoundary>
      )}
    </>
  );
};

export const ReclaimAction: FC<WeldActionProps> = ({ status, disabled, weldCommodity }) => {
  const { visible, open, close } = useSideSheetState(false);

  return (
    <Fragment>
      <WeldActionButtonStyled disabled={disabled} status={status} onClick={open} data-testid="weld-reclaim-action">
        <ActionContent label={t`Reclaim`} icon="undo" />
      </WeldActionButtonStyled>
      <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
        {visible && (
          <WeldClaim close={close} weld={weldCommodity} isReclaim={true} refetchQueries={reclaimRefetchQueries} />
        )}
      </Suspense>
    </Fragment>
  );
};

export const ResolveBlockAction: FC<WeldActionProps> = ({ status, openBlockSidesheet }) => {
  return (
    <Fragment>
      <WeldActionButtonStyled status={status} onClick={openBlockSidesheet} data-testid="resolve-block-action">
        <ActionContent label={t`Resolve block`} icon="exclamation-triangle" />
      </WeldActionButtonStyled>
    </Fragment>
  );
};

export const WeldAssignAction: FC<WeldActionProps> = ({ milestone, status, actionText, disabled, weldCommodity }) => {
  const { visible, open, close } = useSideSheetState(false);
  return (
    <Fragment>
      <WeldActionButtonStyled disabled={disabled} status={status} onClick={open} data-testid="weld-assign-action">
        <ActionContent label={actionText || t`Assign/Unassign`} icon="user-plus" />
      </WeldActionButtonStyled>
      <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
        {visible && <WeldMilestoneAssign weldCommodity={weldCommodity} milestone={milestone} onClose={close} />}
      </Suspense>
    </Fragment>
  );
};

const NonAction: FC<WeldNonActionProps> = ({ icon, label }) => {
  return (
    <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }} p={{ sm: '400' }} flexGrow={{ sm: 1 }}>
      <Box mr={{ sm: '400' }}>
        <Icon icon={icon} />
      </Box>
      <Text size="300" color="grey.1000">
        {label}
      </Text>
    </Box>
  );
};

export const RejectWeld: FC<WeldSendBackProps> = ({ disabled, openSendBackSideSheet }) => {
  return (
    <Fragment>
      <SecondaryActionButtonStyled
        onClick={openSendBackSideSheet}
        data-testid="weld-Send-back-action"
        disabled={disabled}
      >
        <ActionContent label={t`Send Back`} icon="minus" />
      </SecondaryActionButtonStyled>
    </Fragment>
  );
};

export const ApproveWeldAction: FC<WeldActionProps> = ({ status, customStyle, milestone, weldCommodity }) => {
  const [approveWeldError, setApproveWeldError] = useState<WeldActionErrorExt | null>();
  const dispatchToast = useToastsDispatch();
  const onCompleted = (data) => {
    const error = get(data, 'approveWeld.error');
    if (error) {
      setApproveWeldError(error);
    } else {
      setApproveWeldError(null);
      const toastAction: ToastAction = {
        type: 'ADD_TOAST',
        payload: {
          toastType: ToastType.INFO,
          content: t`Approved Weld successfully`,
          autoDismissTimeout: 5000,
        },
      };
      dispatchToast(toastAction);
      close();
    }
  };
  const onError = (error) => {
    setApproveWeldError(error);
  };
  const [approveWeld, { loading }] = useApproveWeld(onCompleted, onError, approveWeldRefetchQueries);

  const closeModal = () => {
    setApproveWeldError(null);
  };
  return (
    <Fragment>
      <WeldActionButtonStyled
        status={status}
        disabled={loading}
        isInverted={true}
        data-testid="weld-approve-action"
        style={{ ...customStyle }}
        onClick={async () => {
          await approveWeld({
            variables: { input: { commodityMilestoneId: milestone.id } },
          });
        }}
      >
        {loading && <Spinner size={SpinnerSize.SMALL} />}
        {!loading && <ActionContent label={t`Approve`} icon="check" />}
      </WeldActionButtonStyled>
      {approveWeldError && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <WeldActionError error={approveWeldError} close={closeModal} commodityId={weldCommodity?.id} />
          </Suspense>
        </ErrorBoundary>
      )}
    </Fragment>
  );
};

export const ApproveFitupAction: FC<WeldActionProps> = ({ status, customStyle, milestone, weldCommodity }) => {
  const [approveFitupError, setApproveFitupError] = useState<WeldActionErrorExt | null>();
  const dispatchToast = useToastsDispatch();
  const onCompleted = (data) => {
    const error = get(data, 'approveFitup.error');
    if (error) {
      setApproveFitupError(error);
    } else {
      setApproveFitupError(null);
      const toastAction: ToastAction = {
        type: 'ADD_TOAST',
        payload: {
          toastType: ToastType.INFO,
          content: t`Approved Fitup successfully`,
          autoDismissTimeout: 5000,
        },
      };
      dispatchToast(toastAction);
      close();
    }
  };
  const onError = (error) => {
    setApproveFitupError(error);
  };
  const approveFitupQueries = fitupQueries();
  const [approveFitup, { loading }] = useApproveFitup(onCompleted, onError, approveFitupQueries);

  const closeModal = () => {
    setApproveFitupError(null);
  };
  return (
    <Fragment>
      <WeldActionButtonStyled
        status={status}
        disabled={loading}
        isInverted={true}
        data-testid="fitup-approve-action"
        style={{ ...customStyle }}
        onClick={async () => {
          await approveFitup({
            variables: { input: { commodityMilestoneId: milestone.id } },
          });
        }}
      >
        {loading && <Spinner size={SpinnerSize.SMALL} />}
        {!loading && <ActionContent label={t`Approve`} icon="check" />}
      </WeldActionButtonStyled>
      {approveFitupError && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <WeldActionError error={approveFitupError} close={closeModal} commodityId={weldCommodity?.id} />
          </Suspense>
        </ErrorBoundary>
      )}
    </Fragment>
  );
};

export const ApproveRejectFitupAction: FC<WeldActionProps> = ({
  milestone,
  status,
  weldCommodity,
  disabled,
  openSendBackSideSheet,
}) => {
  const assignedCrewMembers: CommodityAssignee[] = useWeldAssignees(weldCommodity?.commodityAssignees);
  const { canApproveFitup, canRejectFitup } = useWeldPermissions(assignedCrewMembers);
  return (
    <Fragment>
      {canRejectFitup && (
        <RejectWeld
          disabled={disabled}
          weldCommodity={weldCommodity}
          openSendBackSideSheet={openSendBackSideSheet}
        ></RejectWeld>
      )}
      {canApproveFitup && <ApproveFitupAction milestone={milestone} status={status} />}
    </Fragment>
  );
};

export const ApproveRejectAction: FC<WeldActionProps> = ({
  milestone,
  status,
  weldCommodity,
  disabled,
  openSendBackSideSheet,
}) => {
  const assignedCrewMembers: CommodityAssignee[] = useWeldAssignees(weldCommodity?.commodityAssignees);
  const { canApproveWeld, canRejectWeld } = useWeldPermissions(assignedCrewMembers);

  return (
    <Fragment>
      {canRejectWeld && (
        <RejectWeld
          disabled={disabled}
          weldCommodity={weldCommodity}
          openSendBackSideSheet={openSendBackSideSheet}
        ></RejectWeld>
      )}
      {canApproveWeld && <ApproveWeldAction milestone={milestone} status={status} weldCommodity={weldCommodity} />}
    </Fragment>
  );
};
export const ApproveUnclaimAction: FC<WeldActionProps> = ({ status, milestone, disabled, weldCommodity }) => {
  const assignedCrewMembers: CommodityAssignee[] = useWeldAssignees(weldCommodity?.commodityAssignees);
  const { canApproveWeld, canClaimWeld } = useWeldPermissions(assignedCrewMembers);
  return (
    <Fragment>
      {canClaimWeld && (
        <>
          <UnclaimAction disabled={disabled} milestone={milestone} status={status}></UnclaimAction>
        </>
      )}
      {canApproveWeld && <ApproveWeldAction milestone={milestone} status={status} />}
    </Fragment>
  );
};
export const UnapproveWeldAction: FC<WeldActionProps> = ({ status, customStyle, milestone, weldCommodity }) => {
  const [unApproveWeldError, setUnApproveWeldError] = useState<WeldActionErrorExt | null>();
  const dispatchToast = useToastsDispatch();
  const onCompleted = (data) => {
    const error = get(data, 'unApproveWeld.error');
    if (error) {
      setUnApproveWeldError(error);
    } else {
      setUnApproveWeldError(null);
      const toastAction: ToastAction = {
        type: 'ADD_TOAST',
        payload: {
          toastType: ToastType.INFO,
          content: t`Unapproved Weld successfully`,
          autoDismissTimeout: 5000,
        },
      };
      dispatchToast(toastAction);
      close();
    }
  };
  const onError = (error) => {
    setUnApproveWeldError(error);
  };
  const [unApproveWeld, { loading }] = useUnApproveWeld(onCompleted, onError, unapproveWeldRefetchQueries);

  const closeModal = () => {
    setUnApproveWeldError(null);
  };
  return (
    <>
      <WeldActionButtonStyled
        disabled={loading}
        status={status}
        isInverted={true}
        style={customStyle}
        data-testid="weld-unapprove-action"
        onClick={async () => {
          await unApproveWeld({
            variables: { input: { commodityMilestoneId: milestone.id } },
          });
        }}
      >
        {loading && <Spinner size={SpinnerSize.SMALL} />}
        {!loading && <ActionContent label={t`Unapprove`} icon="minus" />}
      </WeldActionButtonStyled>
      {unApproveWeldError && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <WeldActionError error={unApproveWeldError} close={closeModal} commodityId={weldCommodity?.id} />
          </Suspense>
        </ErrorBoundary>
      )}
    </>
  );
};

export const UnapproveFitupAction: FC<WeldActionProps> = (props) => {
  const { status, milestone, customStyle, weldCommodity } = props;
  const [unApproveFitupError, setUnApproveFitupError] = useState<WeldActionErrorExt | null>();
  const onCompleted = (data) => {
    const error = get(data, 'unapproveFitup.error');
    if (error) {
      setUnApproveFitupError(error);
    } else {
      setUnApproveFitupError(null);
      close();
    }
  };
  const onError = (error) => {
    setUnApproveFitupError(error);
  };
  const unapproveFitupQueries = fitupQueries();
  const [unapproveFitup, { loading }] = useUnapproveFitup(onCompleted, onError, unapproveFitupQueries);

  const closeModal = () => {
    setUnApproveFitupError(null);
  };
  return (
    <>
      <WeldActionButtonStyled
        status={status}
        disabled={loading}
        isInverted={true}
        data-testid="fitup-unapprove-action"
        style={{ ...customStyle }}
        onClick={async () => {
          await unapproveFitup({
            variables: { input: { commodityMilestoneId: milestone.id } },
          });
        }}
      >
        {loading && <Spinner size={SpinnerSize.SMALL} />}
        {!loading && <ActionContent label={t`Unapprove`} icon="minus" />}
      </WeldActionButtonStyled>
      {unApproveFitupError && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <WeldActionError error={unApproveFitupError} close={closeModal} commodityId={weldCommodity?.id} />
          </Suspense>
        </ErrorBoundary>
      )}
    </>
  );
};

const Action: FC<WeldActionProps> = (props) => {
  const { i18n } = useLingui();
  const { user } = useAppUser();
  const { weldCommodity, milestone, status, openBlockSidesheet } = props;
  const activityHistory = weldCommodity?.milestonesActivityHistory?.items ?? [];
  const currentMilestoneAtivityHistory = filter(activityHistory, (item: CommodityMilestoneActivity) => {
    return item.milestoneName === milestone.name;
  });
  const sortedCurrentMilestoneAtivityHistory = currentMilestoneAtivityHistory
    ? [...currentMilestoneAtivityHistory]?.sort(
        (a, b) => new Date(b?.dateTime)?.getTime() - new Date(a?.dateTime)?.getTime() || b?.orderBy - a?.orderBy
      )
    : [];
  const latestClaimActivity = find(
    sortedCurrentMilestoneAtivityHistory,
    (item) => item.activity === MilestoneActivity.CLAIMED
  );
  const isUserClaimer = latestClaimActivity ? latestClaimActivity.crewMember?.id === user?.id : false;
  const assignedCrewMembers: CommodityAssignee[] = useWeldAssignees(weldCommodity?.commodityAssignees);
  const { canClaimWeld, canApproveWeld, canRejectWeld, canApproveFitup, canRejectFitup } =
    useWeldPermissions(assignedCrewMembers);
  const isOwner = useWorkPackageOwner(assignedCrewMembers);
  const workPackageOwners = [
    ...(weldCommodity?.workPackage?.primaryOwners ?? []),
    ...(weldCommodity?.workPackage?.secondaryOwners ?? []),
    ...(weldCommodity?.workPackage?.otherOwners ?? []),
  ];
  const isCommodityOwner = useWeldOwner(assignedCrewMembers);
  const { canResolveBlock } = useBlockPermission(
    workPackageOwners,
    [weldCommodity?.facility],
    null,
    assignedCrewMembers
  );

  const createdByUserId = get(milestone, 'currentStatus.createdBy.id');
  const createdAt = get(milestone, 'currentStatus.createdAt');
  const parsedCreatedAt = createdAt ? parseISO(createdAt) : undefined;
  const currentProjectWeek = find(user?.project?.projectConfiguration?.projectWeeks, (projectWeek) =>
    projectWeek?.isCurrent ? true : false
  );
  const currentWeekStartDate = parseISO(currentProjectWeek?.startDate);
  const getClaimActionContent = () => {
    if (canClaimWeld && !canApproveWeld) return <ClaimAction {...props} />;
    if (canClaimWeld && canApproveWeld) return <ClaimAndApproveAction {...props} />;
    return null;
  };
  const unclaimProps = {
    ...props,
    disabled:
      (isOwner || isCommodityOwner) &&
      createdAt &&
      isBefore(new Date(createdAt), new Date(addDays(new Date(), -2).toISOString())),
  };
  switch (status) {
    case CommodityStatusCode.CLAIMED:
      // CLAIMED -> UNCLAIM
      // claimed by user and hence can always unclaim!
      if (isUserClaimer && !canApproveWeld) {
        if (canClaimWeld) return <UnclaimAction {...unclaimProps} />;
      }
      // CLAIMED -> APPROVE / REJECT
      // Other user claimed, user gets option to Reject / Approve
      if (!isUserClaimer && (canApproveWeld || canRejectWeld)) return <ApproveRejectAction {...props} />;
      // CLAIMED -> APPROVE / UNCLAIM
      // FE claimed, they get option to Unclaim / Approve
      if (isUserClaimer && (canApproveWeld || canClaimWeld)) return <ApproveUnclaimAction {...unclaimProps} />;
      if (isOwner || isCommodityOwner) return <UnclaimAction {...unclaimProps} />;
      return <NonAction icon="check" label={t`Claimed`} />;
    case CommodityStatusCode.VT_APPROVED:
      // APPROVED -> UNAPPROVE
      if (
        canApproveWeld &&
        user?.id === createdByUserId &&
        parsedCreatedAt &&
        !isBefore(parsedCreatedAt, currentWeekStartDate)
      ) {
        return <UnapproveWeldAction {...props} />;
      }
      // NO ACTION: display only
      return <NonAction icon="check" label={t`Approved`} />; // display only
    case CommodityStatusCode.VT_SENT_BACK:
      // REJECTED -> CLAIM
      if (canClaimWeld) return <ReclaimAction {...props} />;
      // NO ACTION: display only
      return <NonAction icon="times" label={t`Rejected`} />; // display only
    case CommodityStatusCode.BLOCKED:
      // BLOCKED -> RESOLVE
      if (canResolveBlock) return <ResolveBlockAction {...props} openBlockSidesheet={openBlockSidesheet} />;
      return <NonAction icon="times" label={t`Blocked`} />; // display only
    case CommodityStatusCode.SENT_BACK_TO_PIPING:
      return null;
    case CommodityStatusCode.FITUP_INSPECTION:
      if (canApproveFitup && !canRejectFitup) return <ApproveFitupAction {...props} />;
      if (canApproveFitup && canRejectFitup) return <ApproveRejectFitupAction {...props} />;
      return <NonAction icon="check" label={t`Fitup Inspection`} />; // display only
    case CommodityStatusCode.READY_TO_WELD:
      if (milestone.currentStatus.codeI18n === MilestoneStatusCode.APPROVED) {
        if (
          canApproveFitup &&
          user?.id === createdByUserId &&
          parsedCreatedAt &&
          !isBefore(parsedCreatedAt, currentWeekStartDate)
        ) {
          return <UnapproveFitupAction {...props} />;
        }
        <NonAction icon="check" label={t`Ready to Weld`} />;
      }
      return getClaimActionContent();
    default:
      <NonAction icon="check" label={i18n._(WeldStatusMessages[status])} />;
  }
};

export const WeldAction: FC<WeldActionProps> = (props) => {
  const { milestone, weldCommodity, fitupProjectMilestoneDetails } = props;
  const { id = ' ', tag = ' ' } = weldCommodity?.workPackage ? weldCommodity.workPackage : {};
  const { visible, open, close } = useSideSheetState(false);
  const {
    visible: visibleSendBackSideSheet,
    open: openSendBackSideSheet,
    close: closeSendBackSideSheet,
  } = useSideSheetState(false);

  const currentMilestone = weldCommodity?.milestones.find((milestone) => {
    return milestone?.type?.id === weldCommodity?.currentMilestoneType?.id;
  });

  const assignedCrewMembers = [
    ...(weldCommodity?.workPackage?.primaryOwners ?? []),
    ...(weldCommodity?.workPackage?.secondaryOwners ?? []),
    ...(weldCommodity?.workPackage?.otherOwners ?? []),
  ];
  const weldCommodityAssignees: CommodityAssignee[] = useWeldAssignees(weldCommodity?.commodityAssignees);

  const fitUpMilestone = weldCommodity?.milestones.find((milestone) => {
    return milestone?.name?.toUpperCase() === WeldMilestoneType.FITUP;
  });
  return (
    <>
      <ErrorBoundary>
        <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
          {visible && (
            <ResolveBlock
              assignedCrewMembers={assignedCrewMembers}
              weldCommodityAssignees={weldCommodityAssignees}
              workPackageId={id}
              workPackageTag={tag}
              commodityTag={weldCommodity?.tag}
              commodityTypeName={weldCommodity?.type?.name}
              milestone={milestone}
              onClose={close}
            />
          )}
        </Suspense>
        <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
          {visibleSendBackSideSheet && (
            <WeldSendBackSideSheet
              close={closeSendBackSideSheet}
              weld={weldCommodity}
              fitUpMilestone={fitUpMilestone}
              currentMilestone={currentMilestone}
              isSendBackToForeman={weldCommodity?.status?.status === CommodityStatusCode.CLAIMED ? true : false}
              fitupProjectMilestoneDetails={fitupProjectMilestoneDetails}
              refetchQueries={
                weldCommodity?.status?.status === CommodityStatusCode.FITUP_INSPECTION ||
                weldCommodity?.status?.status === CommodityStatusCode.CLAIMED ||
                weldCommodity?.status?.status === CommodityStatusCode.READY_TO_WELD ||
                weldCommodity?.status?.status === CommodityStatusCode.VT_SENT_BACK
                  ? refetchQueries
                  : [
                      'commodities',
                      'commodityStats',
                      'commodityWeeklyStats',
                      'commodityStatsPriority',
                      'commodityDashboardTabStats',
                    ]
              }
            />
          )}
        </Suspense>
        <Action {...props} openBlockSidesheet={open} openSendBackSideSheet={openSendBackSideSheet}></Action>
      </ErrorBoundary>
    </>
  );
};
